import { inject, Injectable, OnDestroy } from '@angular/core';
import {
  UiTourDataParams,
  UiTourRegistry,
  UiTourStatus,
  UiTourType,
} from '@booking-booster-client/models';
import {
  camelCase,
  difference,
  isEqual,
  sortBy,
  upperFirst,
} from 'es-toolkit/compat';
import { IStepOption, TourService } from 'ngx-ui-tour-primeng';
import { TourDefaultSettingsService } from '../ui-tours/configs/tour-default-settings-service';
import { combineLatest, filter, Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { select, Store } from '@ngrx/store';
import {
  PreferencesStoreActions,
  PreferencesStoreSelectors,
} from '@booking-booster-client/root-store';
import { AuthService } from './auth.service';
import { TourRegistryMap } from '../ui-tours/tour-registry-map';
import { ConfirmationService } from 'primeng/api';
import { BreakPointObserverService } from './break-point-observer.service';
import { VideoPlayerComponent } from '@booking-booster-client/components';
import { DialogService } from 'primeng/dynamicdialog';
import { PresentationVideoLanguageMap } from '../ui-tours/tour-presentation-video-languages';

const TOUR_REGISTRY_MAP: UiTourRegistry = TourRegistryMap;

const EXCLUDE_STEPS_SCROLL_TO_VIEW = new Set([
  'statistic-section',
  'performance-box',
  'properties-section',
]);

const PRESENTATION_VIDEO_LANGUAGE_MAP = PresentationVideoLanguageMap;

@Injectable({ providedIn: 'root' })
export class TourStepsRegistryService implements OnDestroy {
  private tourService = inject(TourService);

  private tourDefaultSettingsService = inject(TourDefaultSettingsService);

  private router = inject(Router);

  private translateService = inject(TranslateService);

  private store = inject(Store);

  private dialogService = inject(DialogService);

  private authService = inject(AuthService);

  private confirmationService = inject(ConfirmationService);

  private breakPointMatch = inject(BreakPointObserverService);

  private subs = new Subscription();

  private endFn? = () => {
    return;
  };

  private excludeStepsScrollToView = EXCLUDE_STEPS_SCROLL_TO_VIEW;

  private tourType!: UiTourType;

  private isAdmin = false;

  private lastVisitedStepId = '';

  private tourRegistryMap = TOUR_REGISTRY_MAP;

  private currentStep = '';

  private isExclusiveTourMode = false;

  private virtualTourPreferences!: UiTourStatus;

  private temporaryDataStartTour: UiTourDataParams | null = null;

  private shouldSeeIntroVideo = false;

  readonly virtualTourPreferences$ = this.store.pipe(
    select(PreferencesStoreSelectors.selectVirtualTourPreferences),
  );

  readonly preferencesLoaded$ = this.store.pipe(
    select(PreferencesStoreSelectors.selectArePreferencesLoaded),
  );

  isMobile = false;

  isPortraitTablet = false;

  constructor() {
    this.initSubscriptions();

    this.subs.add(
      this.breakPointMatch
        .isMatch('beforeMedium')
        .subscribe((isMatch) => (this.isMobile = isMatch)),
    );

    this.subs.add(
      this.breakPointMatch
        .isMatch('beforeLarge')
        .subscribe((isMatch) => (this.isPortraitTablet = isMatch)),
    );
  }

  initSubscriptions() {
    this.subs.add(
      this.authService.isAdmin$.subscribe(
        (isAdmin) => (this.isAdmin = isAdmin),
      ),
    );

    this.subs.add(
      combineLatest([this.virtualTourPreferences$, this.preferencesLoaded$])
        .pipe(
          // Procedi solo quando le preferenze sono state caricate
          filter(([, loaded]) => loaded),
        )
        .subscribe(([preferences]) => {
          if (!preferences) {
            this.shouldSeeIntroVideo = true;
          }
          this.virtualTourPreferences = preferences ?? {
            hasSeenIntroTour: {},
          };

          if (this.temporaryDataStartTour) {
            this.startTour(this.temporaryDataStartTour);
          }
        }),
    );

    this.subs.add(
      this.tourService.stepShow$.subscribe(({ step }) => {
        this.lastVisitedStepId = step?.anchorId || '';
        this.scrollToAnchor(step);
      }),
    );

    this.subs.add(
      this.tourService.end$.subscribe(() => {
        const lastStep = this.tourType?.steps[this.tourType.steps.length - 1];

        const isOnLastStep = this.lastVisitedStepId === lastStep?.anchorId;

        this.currentStep = lastStep?.stepId || '';

        if (isOnLastStep && !this.isAdmin) {
          this.updateUiTourUserPreferences({
            nextStep: this.tourType.nextStep,
            context: this.tourType.context,
          });
        }

        if (this.endFn) {
          this.endFn();
        }

        const skipTourfunction = () => {
          this.tourService.end();
        };

        if (
          !this.hasCompletedAllTours() &&
          !this.virtualTourPreferences.toursSkipped &&
          !isOnLastStep &&
          !this.isAdmin
        ) {
          this.skipTourModal(skipTourfunction);
        }
      }),
    );
  }

  tourValidate(tourType: UiTourType): boolean {
    if (tourType.contextIsRequired || tourType.steps[0].contextIsRequired) {
      const validNext =
        tourType.nextStep &&
        tourType.context &&
        this.resolveTemplateWithCheck(
          tourType.nextStep || '',
          tourType.context,
        );

      const validStep =
        tourType.steps?.[0]?.route &&
        tourType.steps?.[0]?.context &&
        this.resolveTemplateWithCheck(
          tourType.steps[0].route as string,
          tourType.steps[0].context,
        );

      if (validNext || validStep) {
        return true;
      } else {
        alert('Context key is wrong');
        return false;
      }
    }
    return true;
  }

  async startTour(data: UiTourDataParams) {
    if (
      (this.isPortraitTablet && this.isMobile) ||
      (this.isAdmin && !data.isExclusiveTourMode)
    ) {
      return;
    }

    if (!this.virtualTourPreferences) {
      this.temporaryDataStartTour = data;
      return;
    }

    if (this.shouldSeeIntroVideo) {
      this.shouldSeeIntroVideo = false;
      this.temporaryDataStartTour = data;
      this.showPresentationVideo(true);
      return;
    }

    const { tourSlug, tourType, isExclusiveTourMode = false, fx, endFx } = data;

    if (!this.tourValidate(tourType)) {
      return;
    }

    this.isExclusiveTourMode = isExclusiveTourMode;

    this.endFn = endFx;

    this.tourType = tourType;

    if (!this.isExclusiveTourMode) {
      if (this.hasCompletedAllTours()) {
        if (!this.hasNewTourSteps(tourSlug)) {
          return;
        }
        this.isExclusiveTourMode = true;
      }

      if (
        this.virtualTourPreferences.toursSkipped &&
        !this.hasNewTourSteps(tourSlug)
      ) {
        return;
      }

      if (this.shouldRedirectToUnseenTour(tourSlug)) {
        this.redirectUnseenRequiredTours();

        return;
      }
    }

    const startAtTour = isExclusiveTourMode
      ? 0
      : this.getStartAtTour({
          preferences: this.virtualTourPreferences,
          slug: tourSlug,
        });

    if (fx) {
      fx();
    }

    setTimeout(() => {
      this.initializeTour({
        tourSlug,
        startTourFx: () => this.tourService.startAt(startAtTour),
      });
    }, 700);

    this.temporaryDataStartTour = null;
  }

  async initializeTour(data: { tourSlug: string; startTourFx: () => void }) {
    const { tourSlug, startTourFx } = data;

    const module = await import(
      `../ui-tours/${tourSlug}/${tourSlug}-tour.config.ts`
    );
    const tour: UiTourType = module[`${this.formatToPascalCase(tourSlug)}Tour`];

    const stepsRoute = this.tourType?.steps[0].context
      ? this.resolveTemplateWithCheck(
          this.tourType?.steps[0].route as string,
          this.tourType?.steps[0].context,
        ) || ''
      : this.tourType?.steps[0].route?.toString() || '';

    const preparedSteps = tour.steps.map((value) => {
      const { titleTranslationKey, contentTranslationKey } = value;

      return {
        ...value,
        title: this.translateService.instant(titleTranslationKey),
        content: this.translateService.instant(contentTranslationKey),
        route: stepsRoute,
      };
    });

    this.waitForAnchorsAndStart(preparedSteps, startTourFx);
  }

  hasNewTourSteps(tourSlug: string): boolean {
    return (
      this.getStartAtTour({
        preferences: this.virtualTourPreferences,
        slug: tourSlug,
      }) > 0
    );
  }

  shouldRedirectToUnseenTour(tourSlug: string): boolean {
    if (this.virtualTourPreferences.toursSkipped) return false;

    const unseenRequiredTours = this.getUnseenRequiredTours();

    if (
      unseenRequiredTours.includes(tourSlug) &&
      unseenRequiredTours.findIndex((tour) => tourSlug === tour) === 0
    ) {
      return false;
    }

    if (unseenRequiredTours?.length) {
      return true;
    }
    return false;
  }

  private async redirectUnseenRequiredTours() {
    const unseenTours = this.getUnseenRequiredTours();

    const firstUnseenSlug = unseenTours?.[0] || '';

    const urlToNavigate =
      this.tourRegistryMap[firstUnseenSlug]?.steps[0]?.route || '';

    this.router.navigate([urlToNavigate]);
  }

  private hasCompletedAllTours(): boolean {
    const savedTourSlugs = Object.keys(
      this.virtualTourPreferences.hasSeenIntroTour,
    );
    return isEqual(sortBy(this.requiredTours()), sortBy(savedTourSlugs));
  }

  private getUnseenRequiredTours(): string[] {
    const savedTourSlugs = Object.keys(
      this.virtualTourPreferences.hasSeenIntroTour,
    );
    return difference(this.requiredTours(), savedTourSlugs);
  }

  private requiredTours() {
    const tourRegistryMapKey = Object.keys(TOUR_REGISTRY_MAP);

    return tourRegistryMapKey.filter(
      (tour) => this.tourRegistryMap[tour]?.viewingRequired,
    );
  }

  private getStartAtTour(data: {
    preferences: UiTourStatus;
    slug: string;
  }): number {
    const { preferences, slug } = data;

    const preferencesTour = preferences.hasSeenIntroTour[slug];

    if (!preferencesTour) return 0;

    const allSteps = this.tourRegistryMap[slug]?.steps ?? [];

    if (!allSteps.length) return 0;

    const lastStepID = allSteps.slice(-1)[0].stepId;

    const { lastStepIDSeen } = preferencesTour;

    if (lastStepID === lastStepIDSeen) {
      return 0;
    }

    return allSteps.findIndex((step) => step.stepId === lastStepIDSeen) || 0;
  }

  private scrollToAnchor(step?: IStepOption) {
    if (!this.excludeStepsScrollToView.has(step?.anchorId || '')) {
      this.scrollToElement();
    }
  }

  private formatToPascalCase(input: string): string {
    return upperFirst(camelCase(input));
  }

  private scrollToElement(): void {
    setTimeout(() => {
      const el = document.querySelector('.custom-modal-ui-tour');

      if (el instanceof HTMLElement) {
        el.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }, 50);
  }

  resolveTemplateWithCheck(
    template: string,
    context: Record<string, string>,
  ): string | null {
    let missing = false;

    const result = template.replace(/\${(.*?)}/g, (_, key) => {
      const value = context[key];
      if (value !== undefined && value !== null) {
        return String(value);
      } else {
        console.error(
          `[Template error] Variabile "${key}" non trovata nel contesto.`,
        );
        missing = true;
        return '';
      }
    });

    return missing ? null : result;
  }

  private updateUiTourUserPreferences(data: {
    nextStep?: string;
    context?: Record<string, string>;
    toursSkipped?: boolean;
  }): void {
    const { slug, steps } = this.tourType;

    const {
      nextStep,
      toursSkipped = this.virtualTourPreferences.toursSkipped || false,
    } = data;

    let hooks = {};

    if (nextStep && !this.isExclusiveTourMode) {
      const nextStepRoute = this.tourType?.context
        ? this.resolveTemplateWithCheck(nextStep, this.tourType?.context)
        : nextStep;

      hooks = {
        onSuccess: () => this.router.navigate([nextStepRoute]),
      };
    }

    const stepsRoute = this.tourType?.steps[0].context
      ? this.resolveTemplateWithCheck(
          steps[0].route as string,
          this.tourType?.steps[0].context,
        ) || ''
      : steps[0].route?.toString() || '';

    const preferencesData: UiTourStatus = {
      toursSkipped,
      hasSeenIntroTour: {
        ...this.virtualTourPreferences.hasSeenIntroTour,
        [slug]: {
          seenAt: new Date(),
          lastStepIDSeen: this.currentStep,
          totalSteps: steps.length,
          urlRoute: stepsRoute,
        },
      },
    };

    this.store.dispatch(
      PreferencesStoreActions.updatePreferencesRequest({
        preferenceKey: 'introTours',
        preferencesData,
        hooks,
      }),
    );
  }

  private skipTourModal(skipTourfunction: () => void) {
    this.confirmationService.confirm({
      message: upperFirst(
        this.translateService.instant('ui_tours.skip_virtual_tour.message'),
      ),
      header: upperFirst(
        this.translateService.instant('ui_tours.skip_virtual_tour.title'),
      ),
      closable: false,
      closeOnEscape: false,
      icon: 'pi pi-exclamation-triangle text-orange-500!',
      rejectButtonProps: {
        label: upperFirst(this.translateService.instant('skip_tour')),
        severity: 'danger',
      },
      acceptButtonProps: {
        label: upperFirst(this.translateService.instant('continue_tour')),
        severity: 'secondary',
        outlined: true,
      },
      accept: () => {
        this.tourService.startAt(this.lastVisitedStepId);
      },
      reject: (closeType: number) => {
        const closeByX = 2;

        if (closeType !== closeByX) {
          this.updateUiTourUserPreferences({ toursSkipped: true });
          skipTourfunction();
        }
      },
    });
  }

  private waitForAnchorsAndStart(
    steps: IStepOption[],
    startTourFx: () => void,
  ) {
    const START_WITH_AT_LEAST_ONE = true; // puoi parametrizzare
    const POLL_EVERY = 150; // ms
    const MAX_WAIT_MS = 8000; // 8s timeout

    let waited = 0;
    const interval = setInterval(() => {
      const registeredAnchors = Object.keys(this.tourService.anchors || {});

      const validSteps = steps.filter(
        (s) => s.anchorId && registeredAnchors.includes(s.anchorId),
      );

      const allRequiredPresent = validSteps.length === steps.length;
      const reachedTimeout = waited >= MAX_WAIT_MS;
      const canStart =
        allRequiredPresent ||
        (START_WITH_AT_LEAST_ONE && validSteps.length > 0);

      if (canStart || reachedTimeout) {
        clearInterval(interval);

        if (!validSteps.length) {
          console.warn(
            '[UI TOUR] Nessuno step valido registrato prima del timeout.',
          );
          return;
        }

        const ordered = steps.filter((s) =>
          validSteps.some((v) => v.anchorId === s.anchorId),
        );

        this.tourService.initialize(
          ordered,
          this.tourDefaultSettingsService.getSettings(),
        );
        startTourFx();
      } else {
        waited += POLL_EVERY;
      }
    }, POLL_EVERY);
  }

  showPresentationVideo(startTourAfterClosing = false) {
    const dialog = this.dialogService.open(VideoPlayerComponent, {
      width: '95vw',
      header: upperFirst(this.translateService.instant('presentation_video')),
      data: {
        source:
          PRESENTATION_VIDEO_LANGUAGE_MAP[this.translateService.currentLang],
      },
      modal: true,
      closable: true,
    });
    dialog.onClose.subscribe(() => {
      if (this.temporaryDataStartTour && startTourAfterClosing) {
        this.startTour(this.temporaryDataStartTour);
      }
    });
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}
